import type { ApolloClient } from '@apollo/client';
import { Prominences, Sentiments } from '@sede-enterprise/shell-ds-fabrics';
import {
  Divider,
  Flexbox,
  FormField,
  Loading,
  SingleDatePicker,
  Sizes,
  Text,
  TextArea,
  Toggle,
  Variants,
} from '@sede-enterprise/shell-ds-react-framework';
import { Action } from '@sede-enterprise/shell-ds-react-framework/build/esm/react-base/src/components/Modal/Modal.types';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import debounce from 'lodash/debounce';
import { getPreviewChartTime } from 'pages/Availability/common/AvailabilityChart/utils/chartDataUtils';
import { AvailabilityInputRow } from 'pages/Availability/common/AvailabilityInputRow';
import {
  performEditSiteAvailabilityMutation,
  performNewSiteAvailabilityMutation,
} from 'pages/Availability/common/availabilitySubmitHandlers';
import { DateTimeSPPicker } from 'pages/Availability/common/DateTimeSPPicker';
import { SITE_FORM_INITIAL_VALUES } from 'pages/Availability/common/FormDrawer/constants';
import { NewSiteAvailabilityEntry } from 'pages/Availability/common/FormDrawer/types';
import { getEnabledFormFields } from 'pages/Availability/common/FormDrawer/utils';
import { ReasonSelect } from 'pages/Availability/common/ReasonSelect';
import {
  getExistingSiteAvailabilityFromBaseSiteAvailabilityEntry,
  getLatestSiteLimit,
  getNewSiteAvailabilityEntryFromBaseAvailabilityEntry,
} from 'pages/Availability/common/utils';
import { DrawerFixed } from 'pages/Availability/SiteLevel/AvailabilityForm.styled';
import { buildAvailabilityFormRules } from 'pages/Availability/SiteLevel/buildAvailabilityFormRules';
import { newAvailabilityEntryValidationSchema } from 'pages/Availability/SiteLevel/siteFormValidation';
import { validatePreview } from 'pages/Availability/SiteLevel/validatePreview';
import { BaseSiteAvailabilityEntry, Site } from 'pages/Availability/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useStore } from 'store';

const disableOpenEnded = true;

interface UseSiteAvailabilityFormDrawerProps {
  site: Site;
  siteSourceKey: string;
  client: ApolloClient;
  title: string;
  width: string;
  editEntry: BaseSiteAvailabilityEntry | null | undefined;
  onRefetch: () => void;
}

interface UseSiteAvailabilityFormDrawerReturn {
  FormDrawer: React.ReactNode;
  previewEntries: BaseSiteAvailabilityEntry[];
}

export const useSiteAvailabilityFormDrawer = ({
  site,
  siteSourceKey,
  client,
  title,
  width,
  editEntry,
  onRefetch,
}: UseSiteAvailabilityFormDrawerProps): UseSiteAvailabilityFormDrawerReturn => {
  const {
    availabilityFormOpen,
    setAvailabilityFormOpen,
    persistedSiteAvailabilityFormData,
    resetPersistedSiteAvailabilityFormData,
    setPersistedSiteAvailabilityFormData,
    setAvailabilityDateRange,
    setAvailabilityUserAction,
    setAlert,
  } = useStore((state) => ({
    availabilityFormOpen: state.availabilityFormOpen,
    setAvailabilityFormOpen: state.setAvailabilityFormOpen,
    persistedSiteAvailabilityFormData: state.persistedSiteAvailabilityFormData,
    resetPersistedSiteAvailabilityFormData:
      state.resetPersistedSiteAvailabilityFormData,
    setPersistedSiteAvailabilityFormData:
      state.setPersistedSiteAvailabilityFormData,
    setAvailabilityDateRange: state.setAvailabilityDateRange,
    setAvailabilityUserAction: state.setAvailabilityUserAction,
    setAlert: state.setAlert,
  }));

  const [isLoading, setIsLoading] = useState(false);
  const [previewEntries, setPreviewEntries] = useState<
    BaseSiteAvailabilityEntry[]
  >([]);

  const latestSiteLimit = getLatestSiteLimit(site?.siteLimits);
  const isEditMode = !!editEntry;
  const persistedFormDataRef = useRef(persistedSiteAvailabilityFormData);
  persistedFormDataRef.current = persistedSiteAvailabilityFormData;

  // Compute initial form values
  const initialFormValues = useMemo<NewSiteAvailabilityEntry>(() => {
    const persistedFormData = persistedFormDataRef.current;
    const hasPersistedData =
      persistedFormData && Object.keys(persistedFormData).length > 0;

    if (!availabilityFormOpen) {
      return SITE_FORM_INITIAL_VALUES;
    }

    // For edit mode, use original entry data if no persisted data
    if (isEditMode && editEntry && !hasPersistedData) {
      return getNewSiteAvailabilityEntryFromBaseAvailabilityEntry(editEntry);
    }

    // Use persisted data if it exists
    if (hasPersistedData && persistedFormData) {
      return {
        ...persistedFormData,
        startImmediately: persistedFormData.startImmediately ?? false,
        isOpenEnded: persistedFormData.isOpenEnded ?? false,
      };
    }

    return SITE_FORM_INITIAL_VALUES;
  }, [availabilityFormOpen, isEditMode, editEntry]);

  const enabledFields = useMemo(
    () => getEnabledFormFields(initialFormValues, isEditMode),
    [initialFormValues, isEditMode],
  );

  const debouncedSetPersistedFormData = useRef(
    debounce(setPersistedSiteAvailabilityFormData, 500),
  ).current;

  const handleEmptyAvailabilityUserAction = useCallback(() => {
    setAvailabilityUserAction({
      siteAvailabilityEntry: null,
      assetAvailabilityEntry: null,
      actionType: null,
    });
  }, [setAvailabilityUserAction]);

  const handleSetPreview = useCallback(
    (previewData: BaseSiteAvailabilityEntry[]) => {
      setPreviewEntries(previewData);

      if (previewData.length > 0 && previewData[0].start) {
        setAvailabilityDateRange({
          start: getPreviewChartTime(previewData, 'start'),
          end: getPreviewChartTime(previewData, 'end'),
        });
      }
    },
    [setAvailabilityDateRange],
  );

  const handleFormClose = useCallback(() => {
    setAvailabilityFormOpen(false);
    setPreviewEntries([]);
    handleEmptyAvailabilityUserAction();
    resetPersistedSiteAvailabilityFormData();
  }, [
    setAvailabilityFormOpen,
    handleEmptyAvailabilityUserAction,
    resetPersistedSiteAvailabilityFormData,
  ]);

  const handleFormSuccess = useCallback(
    (values: NewSiteAvailabilityEntry, type: 'edit' | 'create') => {
      if (values.start && values.end) {
        setAvailabilityDateRange({
          start: values.start,
          end:
            type === 'create' && values.repeatDaily && values.repeatEndInclusive
              ? values.repeatEndInclusive
              : values.end,
        });
      }

      handleFormClose();
      onRefetch();
    },
    [setAvailabilityDateRange, handleFormClose, onRefetch],
  );

  const formik = useFormik<NewSiteAvailabilityEntry>({
    initialValues: initialFormValues,
    validationSchema: newAvailabilityEntryValidationSchema(
      latestSiteLimit?.maxExportPower ?? 0,
      latestSiteLimit?.maxImportPower ?? 0,
      latestSiteLimit?.energyCapacity ?? 0,
      enabledFields,
    ),
    validateOnChange: true,
    validateOnBlur: false,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true);

      let result: {
        success: boolean;
        message: string;
        hasChanges?: boolean;
        count?: number;
      };

      const type = isEditMode ? 'edit' : 'create';

      if (type === 'create') {
        result = await performNewSiteAvailabilityMutation(
          client,
          values,
          siteSourceKey,
        );
      } else {
        const existingEntry =
          getExistingSiteAvailabilityFromBaseSiteAvailabilityEntry(editEntry!);

        result = await performEditSiteAvailabilityMutation(
          client,
          values,
          existingEntry,
        );
      }

      if (result.success) {
        if (type === 'edit' && !result.hasChanges) {
          // Don't show alert for no changes
        } else {
          setAlert?.('success', result.message);
        }
        handleFormSuccess(values, type);
      } else {
        setAlert?.('error', result.message);
      }

      setIsLoading(false);
    },
  });

  // Apply cross-field business rules on top of formik
  const { handlers, helper, fieldEnablement } = buildAvailabilityFormRules(
    formik,
    enabledFields,
    { disableOpenEnded },
  );

  useEffect(() => {
    const { isValid, parsedEntry } = validatePreview(
      formik.values,
      siteSourceKey,
    );
    if (isValid && parsedEntry.length > 0) {
      handleSetPreview(parsedEntry);
      debouncedSetPersistedFormData(formik.values);
    }
  }, [
    formik.values,
    siteSourceKey,
    handleSetPreview,
    debouncedSetPersistedFormData,
  ]);

  const getActions = (isLoading: boolean): Action[] => {
    return [
      {
        label: 'Cancel',
        action: () => {
          formik.resetForm({
            values: SITE_FORM_INITIAL_VALUES,
          });
        },
        showActionButton: !isLoading,
        props: {
          variant: Variants.Outlined,
          sentiment: Sentiments.Primary,
        },
      },
      {
        label: 'Submit',
        action: () => formik.submitForm(),
        showActionButton: !isLoading,
        props: {
          variant: Variants.Filled,
          sentiment: Sentiments.Primary,
        },
      },
    ];
  };

  const FormDrawer = (
    <DrawerFixed
      open={availabilityFormOpen}
      onClose={handleFormClose}
      width={width}
      getContainer={false}
      header={{
        title: title,
        description: 'At site level',
      }}
      sticky={true}
      mask={false}
      elevation={true}
      size={Sizes.Medium}
      actions={getActions(isLoading)}
      closeButton={true}
      zIndex={5}
    >
      <div
        style={{
          height: '100vh',
          padding: '5px',
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          overflow: 'auto',
        }}
      >
        {isLoading ? (
          <Flexbox
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            style={{ height: '100%' }}
          >
            <Loading size={Sizes.XXLarge} />
          </Flexbox>
        ) : (
          <Flexbox flexDirection="column">
            <Flexbox flexDirection="row" columnGap="16px">
              <FormField
                label="Start"
                mandatory={true}
                bottomLeftHelper={helper('start')}
              >
                <DateTimeSPPicker
                  testId="reductionFormStart"
                  ISOStringUTCValue={formik.values.start ?? null}
                  onChange={handlers.start}
                  disableDate={!fieldEnablement.startDateEnabled}
                  disableTime={!fieldEnablement.startTimeEnabled}
                />
              </FormField>

              <FormField
                label="End"
                mandatory={true}
                bottomLeftHelper={helper('end')}
              >
                <DateTimeSPPicker
                  testId="reductionFormEnd"
                  ISOStringUTCValue={formik.values.end ?? null}
                  onChange={handlers.end}
                  disableDate={!fieldEnablement.endDateEnabled}
                  disableTime={!fieldEnablement.endTimeEnabled}
                />
              </FormField>
            </Flexbox>

            <Flexbox flexDirection="row" style={{ marginTop: '16px' }}>
              <div style={{ width: '33%' }}>
                <Toggle
                  data-testid="reductionFormStartImmediately"
                  label="From next SP"
                  checked={formik.values.startImmediately}
                  disabled={!fieldEnablement.startImmediatelyEnabled}
                  onChange={handlers.startImmediately}
                />
              </div>
              <div style={{ width: '33%' }}>
                <Toggle
                  data-testid="reductionFormRepeatDaily"
                  label="Repeat daily"
                  checked={formik.values.repeatDaily}
                  disabled={!fieldEnablement.repeatDailyEnabled}
                  onChange={handlers.repeatDaily}
                />
              </div>
              <div style={{ width: '33%' }}>
                <Toggle
                  data-testid="reductionFormOpenEnded"
                  label="Open ended"
                  disabled={!fieldEnablement.openEndedEnabled}
                  checked={formik.values.isOpenEnded}
                  onChange={handlers.isOpenEnded}
                />
              </div>
            </Flexbox>

            {formik.values.repeatDaily && (
              <Flexbox flexDirection="row" style={{ marginTop: '36px' }}>
                <FormField
                  label="Repeat Until (Inclusive)"
                  mandatory={true}
                  bottomLeftHelper={helper('repeatEndInclusive')}
                >
                  <SingleDatePicker
                    data-testid="reductionFormRepeatEndInclusive"
                    placeholder="DD MM YYYY"
                    value={
                      formik.values.repeatEndInclusive
                        ? dayjs(formik.values.repeatEndInclusive)
                        : null
                    }
                    onChange={(value) => {
                      if (value) {
                        handlers.repeatEndInclusive(
                          value.startOf('day').toISOString(),
                        );
                      }
                    }}
                    disabled={!fieldEnablement.repeatEndInclusiveEnabled}
                  />
                </FormField>
              </Flexbox>
            )}

            <Flexbox flexDirection="column" style={{ marginTop: '36px' }}>
              <FormField
                label="Reason"
                mandatory={true}
                bottomLeftHelper={helper('availabilityReason')}
              >
                <ReasonSelect
                  value={formik.values.availabilityReason}
                  disabled={!fieldEnablement.availabilityReasonEnabled}
                  onChange={handlers.availabilityReason}
                />
              </FormField>

              <FormField mandatory={false}>
                <TextArea
                  disabled={!fieldEnablement.notesEnabled}
                  data-testid="reductionFormComment"
                  placeholder="Additional notes (optional)"
                  resize="vertical"
                  value={formik.values.notes ?? ''}
                  onChange={(e) => handlers.notes(e.target.value)}
                />
              </FormField>
            </Flexbox>

            <div style={{ marginTop: '16px' }}>
              <Text size={Sizes.Small} prominence={Prominences.Subtle}>
                Please enter the limits at which your site can perform according
                to the above reason only. In the case of overlapping reasons,
                please submit a new form to enter limits attached to another
                reason.
              </Text>
            </div>
            <div style={{ marginTop: '8px' }}>
              <Divider size={Sizes.Small} prominence={'subtle'} />
            </div>
            <div style={{ marginTop: '30px' }}></div>

            <AvailabilityInputRow
              testId="reductionFormInputRow"
              disabled={
                !fieldEnablement.maxExportPowerEnabled ||
                !fieldEnablement.maxImportPowerEnabled ||
                !fieldEnablement.energyCapacityEnabled
              }
              maxExportPower={formik.values.maxExportPower}
              maxImportPower={formik.values.maxImportPower}
              energyCapacity={formik.values.energyCapacity}
              errors={formik.errors}
              touched={formik.touched}
              onChange={({
                maxExportPower,
                maxImportPower,
                energyCapacity,
              }: {
                maxExportPower: string | null;
                maxImportPower: string | null;
                energyCapacity: string | null;
              }) => {
                handlers.maxExportPower(maxExportPower);
                handlers.maxImportPower(maxImportPower);
                handlers.energyCapacity(energyCapacity);
              }}
            />
          </Flexbox>
        )}
      </div>
    </DrawerFixed>
  );

  return {
    FormDrawer,
    previewEntries,
  };
};
